import { useQueryClient } from '@tanstack/react-query';
import { MARCA_POR_DEFECTO, aplicarMarca, limpiarMarca, type Marca } from '@tarja/ui';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, decodificarJwt } from '../api/client';
import { api, http } from '../api/tarja';
import type { EnrolamientoMfa, RegistroInput, Usuario } from '../api/types';
import { capacidades, type Capacidad } from './capacidades';

export type EstadoSesion = 'iniciando' | 'anonimo' | 'mfa' | 'autenticado';

interface Claims {
  sub: string;
  tid: string;
  tenants?: string[];
  rb?: Usuario['rol_base'];
  name?: string;
  login?: string;
}

interface ContextoAuth {
  estado: EstadoSesion;
  usuario: Usuario | null;
  /** Presente cuando el usuario debe enrolar su app autenticadora en este ingreso. */
  enrolamientoMfa: EnrolamientoMfa | null;
  marca: Marca;
  caps: Set<Capacidad>;
  puede: (c: Capacidad) => boolean;
  tenantActivo: string | null;
  /** Devuelve 'mfa' si el servidor exige segundo factor. */
  login: (login: string, password: string) => Promise<'ok' | 'mfa'>;
  verificarMfa: (codigo: string) => Promise<void>;
  cancelarMfa: () => void;
  /** Crea cuenta + empresa. Devuelve 'pendiente' si la empresa quedó registrada pero su base aún no está habilitada (sin sesión). */
  registrar: (datos: RegistroInput) => Promise<'ok' | 'pendiente'>;
  /** Acepta una invitación (define contraseña) y abre sesión. */
  aceptarInvitacion: (datos: { token: string; nombre?: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  cambiarTenant: (tenantId: string) => Promise<void>;
  recargarMarca: () => Promise<void>;
}

const Ctx = createContext<ContextoAuth | null>(null);

const CLAVE_TENANT = 'tarja.tenant';

function leerTenantGuardado(): string | null {
  try {
    return window.localStorage.getItem(CLAVE_TENANT);
  } catch {
    return null;
  }
}

/** Usuario mínimo a partir de los claims del JWT, si el API no expone /auth/me. */
function usuarioDesdeToken(token: string): Usuario | null {
  const c = decodificarJwt<Claims>(token);
  if (!c) return null;
  return {
    usuario_id: c.sub,
    login: c.login ?? c.sub,
    nombre: c.name ?? c.login ?? 'Usuario',
    rol_base: c.rb ?? 'jefe',
    tenant_id: c.tid,
    tenants: (c.tenants ?? [c.tid]).map((t) => ({ tenant_id: t, nombre: t })),
    permisos: [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<EstadoSesion>('iniciando');
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [marca, setMarca] = useState<Marca>(MARCA_POR_DEFECTO);
  const [tenantActivo, setTenantActivo] = useState<string | null>(null);
  const mfaToken = useRef<string | null>(null);
  const [enrolamientoMfa, setEnrolamientoMfa] = useState<EnrolamientoMfa | null>(null);

  const cerrarLocal = useCallback(() => {
    http.setAccessToken(null);
    http.setTenant(null);
    setUsuario(null);
    setTenantActivo(null);
    setEstado('anonimo');
    limpiarMarca();
    setMarca(MARCA_POR_DEFECTO);
    qc.clear();
  }, [qc]);

  const recargarMarca = useCallback(async () => {
    try {
      const m = await api.admin.marca();
      const completa = { ...MARCA_POR_DEFECTO, ...m };
      setMarca(completa);
      aplicarMarca(completa);
    } catch {
      aplicarMarca(MARCA_POR_DEFECTO);
    }
  }, []);

  /** Tras obtener un token de acceso: perfil, tenant y marca. */
  const completarSesion = useCallback(
    async (accessToken: string) => {
      http.setAccessToken(accessToken);
      let u: Usuario | null = null;
      try {
        u = await api.auth.me();
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 404) throw e;
        u = usuarioDesdeToken(accessToken);
      }
      if (!u) throw new ApiError({ status: 401, code: 'AUTH_TOKEN_INVALIDO' });
      // X-Tenant-Id solo cuando el usuario tiene varios tenants (§1.3).
      if (u.tenants.length > 1) {
        const guardado = leerTenantGuardado();
        const elegido = u.tenants.some((t) => t.tenant_id === guardado) ? guardado! : u.tenant_id;
        http.setTenant(elegido);
        setTenantActivo(elegido);
      } else {
        http.setTenant(null);
        setTenantActivo(u.tenant_id);
      }
      setUsuario(u);
      await recargarMarca();
      setEstado('autenticado');
    },
    [recargarMarca],
  );

  useEffect(() => {
    http.setSesionExpirada(() => cerrarLocal());
    let vivo = true;
    // Restaura la sesión desde la cookie HttpOnly de refresh (recarga de página).
    http
      .refrescar()
      .then(async (ok) => {
        if (!vivo) return;
        if (ok && http.getAccessToken()) await completarSesion(http.getAccessToken()!);
        else setEstado('anonimo');
      })
      .catch(() => vivo && setEstado('anonimo'));
    return () => {
      vivo = false;
    };
  }, [cerrarLocal, completarSesion]);

  const login = useCallback(
    async (loginTxt: string, password: string): Promise<'ok' | 'mfa'> => {
      try {
        const r = await api.auth.login({ login: loginTxt, password });
        if ('mfa_requerido' in r && r.mfa_requerido) {
          mfaToken.current = r.mfa_token;
          setEnrolamientoMfa(r.mfa_enrolamiento ?? null);
          setEstado('mfa');
          return 'mfa';
        }
        if ('access_token' in r) {
          await completarSesion(r.access_token);
          return 'ok';
        }
        throw new ApiError({ status: 500, code: 'HTTP_500', detail: 'Respuesta de login inesperada' });
      } catch (e) {
        // Variante: 401 AUTH_MFA_REQUERIDO con mfa_token en el problem detail.
        if (e instanceof ApiError && e.code === 'AUTH_MFA_REQUERIDO' && typeof e.problem?.mfa_token === 'string') {
          mfaToken.current = e.problem.mfa_token;
          setEstado('mfa');
          return 'mfa';
        }
        throw e;
      }
    },
    [completarSesion],
  );

  const verificarMfa = useCallback(
    async (codigo: string) => {
      if (!mfaToken.current) throw new ApiError({ status: 401, code: 'AUTH_MFA_REQUERIDO' });
      const r = await api.auth.verificarMfa(mfaToken.current, codigo);
      mfaToken.current = null;
      setEnrolamientoMfa(null);
      await completarSesion(r.access_token);
    },
    [completarSesion],
  );

  const cancelarMfa = useCallback(() => {
    mfaToken.current = null;
    setEnrolamientoMfa(null);
    setEstado('anonimo');
  }, []);

  const registrar = useCallback(
    async (datos: RegistroInput): Promise<'ok' | 'pendiente'> => {
      const r = await api.auth.registrar(datos);
      if ('access_token' in r) {
        await completarSesion(r.access_token);
        return 'ok';
      }
      return 'pendiente';
    },
    [completarSesion],
  );

  const aceptarInvitacion = useCallback(
    async (datos: { token: string; nombre?: string; password: string }) => {
      const r = await api.auth.aceptarInvitacion(datos);
      await completarSesion(r.access_token);
    },
    [completarSesion],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      /* la sesión local se cierra igual */
    }
    cerrarLocal();
  }, [cerrarLocal]);

  const cambiarTenant = useCallback(
    async (tenantId: string) => {
      try {
        window.localStorage.setItem(CLAVE_TENANT, tenantId);
      } catch {
        /* almacenamiento no disponible */
      }
      http.setTenant(tenantId);
      setTenantActivo(tenantId);
      // El API resuelve el tenant por solicitud con X-Tenant-Id (§1.3); se recargan perfil, permisos y marca.
      qc.clear();
      const token = http.getAccessToken();
      if (token) await completarSesion(token);
    },
    [qc, completarSesion],
  );

  const caps = useMemo(() => capacidades(usuario), [usuario]);
  const valor = useMemo<ContextoAuth>(
    () => ({
      estado,
      usuario,
      enrolamientoMfa,
      marca,
      caps,
      puede: (c) => caps.has(c),
      tenantActivo,
      login,
      verificarMfa,
      cancelarMfa,
      registrar,
      aceptarInvitacion,
      logout,
      cambiarTenant,
      recargarMarca,
    }),
    [estado, usuario, enrolamientoMfa, marca, caps, tenantActivo, login, verificarMfa, cancelarMfa, registrar, aceptarInvitacion, logout, cambiarTenant, recargarMarca],
  );
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): ContextoAuth {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth fuera de AuthProvider');
  return c;
}
